import { Table } from 'antd';
import type { ColumnFilterItem, FilterDropdownProps } from 'antd/lib/table/interface';
import { lowerFirst, pick, uniqBy } from 'lodash/fp';
import { type Key, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type ModuleListDataFragment } from '../../../api/fragments/ModuleListData';
import { useListModulesQuery } from '../../../api/queries/listModules';
import { useListModuleTypeQuery } from '../../../api/queries/listModuleType';
import { ModuleSortingField, ModuleType } from '../../../api/types';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import FilterBox from '../../../components/FilterBox';
import { type PaginatedTableProps } from '../../../components/PaginatedTable';
import PaginatedTableWithContext from '../../../components/PaginatedTableWithContext';
import InternalErrorResult from '../../../components/results/InternalErrorResult';
import useSearchInDropDown from '../../../components/useSearchInDropDown';
import makeGetSortingRule from '../../../utilities/makeGetSortingRule';
import renderFilterIcon from '../../../utilities/renderFilterIcon';
import renderSearchIcon from '../../../utilities/renderSearchIcon';
import useSortAndFilterCache, { SortAndFilterCacheKey } from '../../../utilities/useSortAndFilterCache';
import { getTitleForType } from '../ModuleDetailsPage/modules';
import useListReducer from './useListReducer';

type ModuleDataSource = ModuleListDataFragment & { key: Key };
type PaginatedModulesTable = PaginatedTableProps<ModuleDataSource>;

const ModuleList = () => {
    const { t } = useTranslation(['moduleList', 'moduleDetails']);
    const navigate = useNavigate();
    const searchBox = useSearchInDropDown();

    const [currentCache, setCache] = useSortAndFilterCache(SortAndFilterCacheKey.Module);
    // get state from a reducer
    const [state, dispatch] = useListReducer(currentCache);

    // get company context
    const company = useCompany(true);

    const getSortField = makeGetSortingRule((field): ModuleSortingField => {
        switch (field) {
            case 'displayName':
                return ModuleSortingField.DisplayName;

            case 'company.displayName':
                return ModuleSortingField.CompanyName;

            case '__typename':
                return ModuleSortingField.ModuleType;

            default:
                throw new Error('Unknown Sorting Field!');
        }
    });

    // fetch data
    const { page, pageSize, filter, sort } = state;
    const { data, loading, error } = useListModulesQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            sort: pick(['field', 'order'], sort),
            filter: { ...filter, companyId: company?.id },
        },
    });

    const { data: listModuleType } = useListModuleTypeQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: { companyId: company?.id },
        },
    });

    const [moduleTypes] = useMemo(() => [listModuleType?.types || []], [listModuleType]);

    // prepare items as a data source
    const [dataSource, total] = useMemo(() => [data?.page?.items || [], data?.page?.count ?? 0], [data]);
    const moduleTypeList = useMemo(
        () =>
            uniqBy(
                'value',
                moduleTypes.map(type => ({
                    text: t(`common:options.moduleTypes.${lowerFirst(type)}`),
                    value: ModuleType[type],
                }))
            ).sort((a, b) => (a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1)),
        [moduleTypes, t]
    );

    const onRow = useCallback<PaginatedModulesTable['onRow']>(
        record => ({
            onClick: () => {
                setCache(state);
                navigate(record.id);
            },
        }),
        [navigate, setCache, state]
    );

    const onChange = useCallback(
        (pagination, filters, sorter, extra) => {
            switch (extra.action) {
                case 'sort':
                    return dispatch({
                        type: 'setSort',
                        sortBy: getSortField(sorter),
                    });

                case 'filter':
                    return dispatch({
                        type: 'setFilter',
                        filterBy: {
                            moduleTypes: !filters.__typename ? null : filters.__typename,
                            displayName: !filters.displayName ? null : filters.displayName[0],
                            companyName: !filters.companyName ? null : filters.companyName[0],
                        },
                    });

                default:
                    return undefined;
            }
        },
        [dispatch]
    );

    const showFilterDropDown = useCallback(
        (props: FilterDropdownProps, filters: ColumnFilterItem[]) =>
            FilterBox({
                filterDropDownProps: props,
                filters,
            }),
        []
    );

    if (!loading) {
        if (error) {
            return <InternalErrorResult />;
        }
    }

    return (
        <PaginatedTableWithContext
            company={company}
            dataSource={dataSource}
            dispatch={dispatch}
            loading={loading}
            onChange={onChange}
            onRow={onRow}
            rowKey="id"
            state={state}
            tableName={t('moduleList:title')}
            total={total}
        >
            {!company && (
                <Table.Column
                    key="companyName"
                    dataIndex={['company', 'displayName']}
                    filterDropdown={searchBox.render('Company Name')}
                    filterIcon={renderSearchIcon}
                    filteredValue={filter.companyName ? [filter.companyName] : undefined}
                    onFilterDropdownOpenChange={searchBox.autoFocus}
                    sortOrder={sort?.field === ModuleSortingField.CompanyName ? sort.orderValue : undefined}
                    title={t('moduleList:columns.companyName')}
                    sorter
                />
            )}
            <Table.Column
                key="displayName"
                dataIndex="displayName"
                filterDropdown={searchBox.render('Display Name')}
                filterIcon={renderSearchIcon}
                filteredValue={filter.displayName ? [filter.displayName] : undefined}
                onFilterDropdownOpenChange={searchBox.autoFocus}
                sortOrder={sort?.field === ModuleSortingField.DisplayName ? sort.orderValue : undefined}
                title={t('moduleList:columns.displayName')}
                sorter
            />
            <Table.Column<ModuleListDataFragment>
                key="__typename"
                dataIndex="__typename"
                filterDropdown={props => showFilterDropDown(props, moduleTypeList)}
                filterIcon={renderFilterIcon}
                filteredValue={filter.moduleTypes}
                render={(value, record) => getTitleForType(record.__typename, t)}
                sortOrder={sort?.field === ModuleSortingField.ModuleType ? sort.orderValue : undefined}
                title={t('moduleList:columns.type')}
                sorter
            />
        </PaginatedTableWithContext>
    );
};

export default ModuleList;
